import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faTrash, faPlus } from '@fortawesome/free-solid-svg-icons';
import { apiService } from '@/utils/api';
import { Site } from '@/types';

const Sites: React.FC = () => {
  const [showModal, setShowModal] = useState(false);
  const [editingSite, setEditingSite] = useState<Site | null>(null);
  const queryClient = useQueryClient();

  // Fetch sites
  const { data: sites = [], isLoading, error } = useQuery('sites', apiService.getSites);

  // Fetch users
  const { data: usersData = [] } = useQuery('users', apiService.getUser);
  
  // Handle both single user and array of users
  const users = Array.isArray(usersData) ? usersData : (usersData ? [usersData] : []);

  // Create/Update site mutation
  const siteMutation = useMutation(
    (siteData: Partial<Site>) => {
      if (editingSite) {
        return apiService.updateSite(editingSite.id, siteData);
      } else {
        return apiService.createSite(siteData);
      }
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('sites');
        setShowModal(false);
        setEditingSite(null);
      },
    }
  );

  // Delete site mutation
  const deleteMutation = useMutation(apiService.deleteSite, {
    onSuccess: () => {
      queryClient.invalidateQueries('sites');
    },
  });

  const handleEdit = (site: Site) => {
    setEditingSite(site);
    setShowModal(true);
  };

  const handleDelete = (id: number) => {
    if (window.confirm('Are you sure you want to delete this site?')) {
      deleteMutation.mutate(id);
    }
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const siteData: Partial<Site> = {
      user_id: parseInt(formData.get('user_id') as string),
      url: formData.get('url') as string,
      organization_number: formData.get('organization_number') as string,
      applications: formData.get('applications') as string,
      email: formData.get('email') as string,
      license_key: formData.get('license_key') as string,
    };

    siteMutation.mutate(siteData);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-danger">
        <div className="flex">
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800">Error</h3>
            <div className="mt-2 text-sm text-red-700">
              Failed to load sites. Please try again.
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">Sites</h1>
        <button
          onClick={() => setShowModal(true)}
          className="px-4 py-2 text-white rounded-md hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 flex items-center space-x-2"
          style={{ backgroundColor: 'hsl(15 100% 52%)' }}
        >
          <FontAwesomeIcon icon={faPlus} className="w-4 h-4" />
          <span>Add Site</span>
        </button>
      </div>

      <div className="card">
        <div className="overflow-x-auto">
          <table className="table">
            <thead>
              <tr>
                <th>ID</th>
                <th>User ID</th>
                <th>URL</th>
                <th>Organization Number</th>
                <th>Email</th>
                <th>License key</th>
                <th className="text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {sites.map((site) => (
                <tr key={site.id}>
                  <td>{site.id}</td>
                  <td>{site.user_id}</td>
                  <td>{site.url}</td>
                  <td>{site.organization_number}</td>
                  <td>{site.email}</td>
                  <td>{site.license_key}</td>
                  <td className="text-right">
                    <div className="flex items-center justify-end space-x-2">
                      <button
                        onClick={() => handleEdit(site)}
                        className="text-primary-600 hover:text-primary-800 text-sm font-medium"
                      >
                        <FontAwesomeIcon icon={faEdit} className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(site.id)}
                        className="text-red-600 hover:text-red-800 text-sm font-medium"
                      >
                        <FontAwesomeIcon icon={faTrash} className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {editingSite ? `Edit site ID#${editingSite.id}` : 'New site'}
              </h3>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="form-label">User ID</label>
                  <select
                    name="user_id"
                    defaultValue={editingSite?.user_id || ''}
                    className="form-input"
                    required
                  >
                    <option value="">Select User</option>
                    {users.map((user) => (
                      <option key={user.id} value={user.id}>
                        {user.name} ({user.email})
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="form-label">URL</label>
                  <input
                    type="text"
                    name="url"
                    defaultValue={editingSite?.url || ''}
                    className="form-input"
                    required
                  />
                </div>

                <div>
                  <label className="form-label">Organization Number</label>
                  <input
                    type="text"
                    name="organization_number"
                    defaultValue={editingSite?.organization_number || ''}
                    className="form-input"
                    required
                  />
                </div>

                <div>
                  <label className="form-label">Applications</label>
                  <input
                    type="text"
                    name="applications"
                    defaultValue={editingSite?.applications || ''}
                    className="form-input"
                  />
                </div>

                <div>
                  <label className="form-label">Email</label>
                  <input
                    type="email"
                    name="email"
                    defaultValue={editingSite?.email || ''}
                    className="form-input"
                    required
                  />
                </div>

                <div>
                  <label className="form-label">License key</label>
                  <input
                    type="text"
                    name="license_key"
                    defaultValue={editingSite?.license_key || ''}
                    className="form-input"
                    required
                  />
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowModal(false);
                      setEditingSite(null);
                    }}
                    className="btn btn-secondary"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={siteMutation.isLoading}
                    className="px-4 py-2 text-white rounded-md hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50"
                    style={{ backgroundColor: 'hsl(15 100% 52%)' }}
                  >
                    {siteMutation.isLoading ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Sites;
